import { useMemo } from "react";
import { diffCounts, diffHunks, lineDiff } from "../../shared/line-diff";

/**
 * What a change does to a text, line by line (AIT-51): removed lines on a red ground, added ones on a green one, three
 * unchanged lines kept around each change and the longer unchanged stretches folded to a count. Used where a request
 * shows exactly what it would write — an agent's instructions and description, every text file of a skill.
 * `before` is "" for a new file and `after` is "" for a removed one. Lines wrap, so it reads at phone width.
 */
export function LineDiff({ before, after, context = 3 }: { before: string; after: string; context?: number }) {
  const lines = useMemo(() => lineDiff(before, after), [before, after]);
  const hunks = useMemo(() => diffHunks(lines, context), [lines, context]);
  const { added, removed } = diffCounts(lines);
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs text-faint">
        <span className="text-success">+{added}</span> <span className="text-danger">−{removed}</span>
      </p>
      <div className="scroll-thin max-h-96 overflow-auto rounded-xl bg-surface py-1.5 font-mono text-xs leading-5 ring-1 ring-line">
        {hunks.map((h, i) =>
          h.kind === "fold" ? (
            <div key={i} className="px-3 py-0.5 text-faint">
              ⋯ {h.count} unchanged {h.count === 1 ? "line" : "lines"}
            </div>
          ) : (
            h.lines.map((l, j) => (
              <div
                key={`${i}-${j}`}
                className={`flex gap-2 px-3 ${l.op === "add" ? "bg-success/10" : l.op === "del" ? "bg-danger/10" : ""}`}
              >
                <span aria-hidden className={`w-3 shrink-0 select-none ${l.op === "add" ? "text-success" : l.op === "del" ? "text-danger" : "text-faint"}`}>
                  {l.op === "add" ? "+" : l.op === "del" ? "−" : " "}
                </span>
                <span className={`min-w-0 flex-1 break-words whitespace-pre-wrap ${l.op === "same" ? "text-muted" : "text-fg"}`}>
                  {l.op === "add" ? <span className="sr-only">Added: </span> : l.op === "del" ? <span className="sr-only">Removed: </span> : null}
                  {l.text || " "}
                </span>
              </div>
            ))
          ),
        )}
      </div>
    </div>
  );
}
